import { storage, STORAGE_KEYS } from './storage.js';
import { SERVER_URL } from '../config.js';
import { saveMetadata, getMetadata } from './indexedDB.js';
import { signInWithGoogle, signOut, getSession, signInWithTwitter, signInWithDiscord, signInWithMagicLink } from './auth_utils.js';
import { tokenManager, TokenManager } from './TokenManager.js';

const IDB_TOKEN_KEY = 'auth_token';
const IDB_USER_KEY = 'user_data';

export class OAuthManager {
    constructor() {
        this._pendingEmailRequest = null;
        this._initializeFromSession();
    }

    async _initializeFromSession() {
        try {
            const token = await tokenManager.getValidAccessToken();
            if (!token) return;

            const userData = await this.getUser();
            if (!userData) return;

            const restoredUser = userData.isLoggedIn === true
              ? userData
              : { ...userData, isLoggedIn: true };
            if (userData.isLoggedIn !== true) {
              tokenManager.setTokens(token, null, restoredUser);
            }
            this._dispatchAuthEvents(restoredUser, 'session_restore');
        } catch (error) {
            console.error('OAuthManager: Init error:', error);
        }
    }

    async startGoogleAuth() {
        try {
            const { url, error } = await signInWithGoogle();
            if (error) throw new Error(error.message || 'Google auth failed');
            if (!url) throw new Error('No Google auth URL');
            window.location.href = url;
        } catch (error) {
            console.error('Google auth failed:', error);
            window.location.href = `${SERVER_URL}/api/v1/auth/google/redirect`;
        }
    }

    async startTwitterAuth() {
        try {
            const { url } = await signInWithTwitter();
            if (!url) throw new Error('No Twitter auth URL');
            window.location.href = url;
        } catch (error) {
            console.error('Twitter auth failed:', error);
            window.location.href = '/?error=auth_failed';
        }
    }

    async startDiscordAuth() {
        try {
            const { url } = await signInWithDiscord();
            if (!url) throw new Error('No Discord auth URL');
            window.location.href = url;
        } catch (error) {
            console.error('Discord auth failed:', error);
            window.location.href = '/?error=auth_failed';
        }
    }

    async startMagicLinkAuth(email) {
        try {
            const response = await signInWithMagicLink(email);
            if (!response.success) throw new Error('Magic link auth failed');
            document.dispatchEvent(new CustomEvent('auth:magiclink:sent', { detail: { email } }));
            return { success: true };
        } catch (error) {
            console.error('Magic link auth failed:', error);
            throw error;
        }
    }

    _resolveAvatarUrl(userLike) {
        if (!userLike || typeof userLike !== 'object') {
            return '/assets/images/characters/unknown.png';
        }
        return userLike.avatar
            || userLike.avatar_url
            || userLike.user_metadata?.avatar_url
            || userLike.user_metadata?.picture
            || '/assets/images/characters/unknown.png';
    }

    async setUserAndToken(token, userData) {
        // Explicit caller payload wins over a freshly-hydrated storage session.
        // (One Tap sets tokens first; getSession() would otherwise clobber avatar.)
        if (token && userData) {
            const finalToken = token.replace(/^["'](.*)["']$/, '$1');
            const userObj = typeof userData === 'string' ? JSON.parse(userData) : userData;

            if (!userObj?.id || !userObj?.email) return false;

            const existingUserData = this._getExistingUserData();
            const finalUserData = {
                ...existingUserData,
                ...userObj,
                name: existingUserData.name || userObj.name || '',
                avatar: this._resolveAvatarUrl({ ...existingUserData, ...userObj }),
                isLoggedIn: true,
                _usernameLoaded: existingUserData._usernameLoaded || false
            };

            tokenManager.setTokens(finalToken, null, finalUserData);
            await this._storeLegacyData(finalToken, finalUserData);
            this._dispatchAuthEvents(finalUserData, 'explicit_login');
            this._syncCryptoAccess();
            return true;
        }

        const session = await getSession();

        if (session) {
            const { access_token: sessionToken, user: sessionUser } = session;
            if (!sessionToken || !sessionUser) return false;

            const existingUserData = this._getExistingUserData();
            const userDataObj = {
                ...existingUserData,
                id: sessionUser.id,
                name: existingUserData.name || sessionUser.name || '',
                email: sessionUser.email,
                avatar: this._resolveAvatarUrl({ ...existingUserData, ...sessionUser }),
                isLoggedIn: true,
                _usernameLoaded: false
            };

            tokenManager.setTokens(sessionToken, null, userDataObj);
            await this._storeLegacyData(sessionToken, userDataObj);
            this._dispatchAuthEvents(userDataObj, 'explicit_login');
            this._syncCryptoAccess();
            return true;
        }

        return true;
    }

    _syncCryptoAccess() {
        import('../config.js').then(({ isSfwSite }) => {
            if (isSfwSite()) return null;
            return import('../create/videoScheduleApi.js');
        }).then((module) => module?.syncMembershipUsage?.()).catch(() => {});
    }

    _getExistingUserData() {
        try {
            return storage.get('userData') || {};
        } catch {
            return {};
        }
    }

    async _storeLegacyData(token, userData) {
        if (token) {
            storage.set(STORAGE_KEYS.TOKEN, token);
            await saveMetadata(IDB_TOKEN_KEY, token).catch(() => {});
        }
        if (userData) {
            storage.setJSON(STORAGE_KEYS.USER, userData);
            await saveMetadata(IDB_USER_KEY, userData).catch(() => {});
        }
    }

    _dispatchAuthEvents(userDataObj, authTrigger = 'explicit_login') {
        document.dispatchEvent(new CustomEvent('auth:login:success', { detail: { user: userDataObj, authTrigger } }));
        window.dispatchEvent(new CustomEvent('usernameUpdated', { detail: { username: userDataObj.name } }));
    }

    async refreshToken() {
        try {
            return !!(await tokenManager.refreshAccessToken());
        } catch {
            return false;
        }
    }

    async getValidToken() {
        try {
            return await tokenManager.getValidAccessToken();
        } catch {
            return null;
        }
    }

    hasRefreshToken() {
        return !!tokenManager.getRefreshToken();
    }

    async isAuthenticated() {
        try {
            if (tokenManager.isAuthenticated()) return true;
            const session = await getSession();
            if (session) return true;
            // The fallback to check localStorage for a token is too permissive, as it could be expired.
            // A user is only authenticated if the token manager or Supabase session confirms it.
            return false;
        } catch {
            return false;
        }
    }

    async getToken() {
        return this.getValidToken();
    }

    async getUser() {
        try {
            const userData = storage.get('userData');
            if (userData) {
                return userData;
            }

            const session = await getSession();
            if (session?.user) {
                const existingUserData = this._getExistingUserData();
                return {
                    id: session.user.id,
                    name: existingUserData.name || '',
                    email: session.user.email,
                    avatar: this._resolveAvatarUrl({ ...existingUserData, ...session.user }),
                    isLoggedIn: true,
                    _usernameLoaded: existingUserData._usernameLoaded || false
                };
            }

            const user = await getMetadata(IDB_USER_KEY);
            if (user) return user;

            return null;
        } catch {
            return null;
        }
    }

    async logout() {
        try {
            // For complete logout, clear userData entirely (don't preserve preferences)
            TokenManager.clearUserData();
            await tokenManager.clearTokens();

            try {
                await signOut();
            } catch {}

            storage.remove(STORAGE_KEYS.TOKEN);

            try {
                await Promise.all([
                    saveMetadata(IDB_TOKEN_KEY, null),
                    saveMetadata(IDB_USER_KEY, null)
                ]);
            } catch {}

            document.dispatchEvent(new CustomEvent('auth:logout', { detail: { source: 'OAuthManager' } }));
            return true;
        } catch {
            return false;
        }
    }
}

let oauthManager = null;

export function initOAuthManager() {
    if (!oauthManager) oauthManager = new OAuthManager();
    return oauthManager;
}
